// chatContext/screenSnapshot.ts
//
// A structured text outline of what the student's page is showing, captured at
// send time and put on the envelope as `screen` (ORA-629). The companion knows
// which route and which content the student is on; it does not know that the
// number beside "Overall QBank Correct" says 72%, so "what does this number
// mean" has been unanswerable.
//
// This is deliberately not a DOM dump. Headings, tables, lists and controls are
// serialized to one line each in their own shape, everything else falls back to
// visible text, and three budgets (characters, nodes, wall time) bound the cost
// so a send from a dense page cannot stall the composer.
//
// Pure: no React, no store, no side effects — `withScreenSnapshot` in
// `currentAppContextEnvelope.ts` decides when it runs.

import { CHAT_SCREEN_MAX_CHARS } from '../types/chatEnvelope';

export interface ScreenSnapshotOptions {
  /** Defaults to `#main-content`, the app's single page container. */
  root?: Element | null;
  maxChars?: number;
  maxNodes?: number;
  timeBudgetMs?: number;
  /** Injected in tests; production reads the performance clock. */
  now?: () => number;
}

/** Visited nodes — elements and text nodes — before the walk gives up. */
const DEFAULT_MAX_NODES = 4000;
/** Wall-clock budget for one capture. */
const DEFAULT_TIME_BUDGET_MS = 15;
/** The clock is read once every this many nodes, not on every node. */
const NODE_CHECK_INTERVAL = 200;
/** Appended whenever any budget cut the walk short. */
const TRUNCATION_LINE = '… (truncated)';
/** Below this the outline says nothing useful, so nothing is sent. */
const MIN_USEFUL_CHARS = 20;
/** A control's label is a label, not a paragraph. */
const MAX_LABEL_CHARS = 120;
/** One line is a line, not a page: a longer one is clamped with an ellipsis. */
const MAX_LINE_CHARS = 400;
/**
 * Below this many characters left, the outline has nothing useful to add and
 * the walk stops. Above it a line that does not fit is skipped and the walk
 * carries on — the heading after a long paragraph is usually worth more than
 * the paragraph.
 */
const MIN_LINE_BUDGET = 40;
const MAX_INPUT_VALUE_CHARS = 80;
const MAX_TABLE_ROWS = 15;
const MAX_LIST_ITEMS = 20;

/** Never walked, and never contributes text. */
const SKIPPED_TAGS = new Set([
  'script',
  'style',
  'noscript',
  'template',
  'svg',
  'canvas',
  'video',
  'audio',
  'iframe',
]);

/**
 * Tags that start their own line. Unknown tags (custom elements, anything not
 * listed) are treated as inline, which is how the renderer treats them too.
 */
const BLOCK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'br',
  'dd',
  'details',
  'dialog',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'header',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'summary',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
]);

const HEADING_LEVELS: Record<string, number> = {
  h1: 1,
  h2: 2,
  h3: 3,
  h4: 4,
  h5: 5,
  h6: 6,
};

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

interface WalkState {
  /** The captured container; nothing outside it may reach the outline. */
  root: Element;
  lines: string[];
  /** Length of `lines` joined by newlines, tracked as we go. */
  chars: number;
  /** Text collected for the line currently being built. */
  pending: string;
  nodes: number;
  truncated: boolean;
  stopped: boolean;
  maxChars: number;
  maxNodes: number;
  deadline: number;
  now: () => number;
}

const collapse = (value: string): string => value.replace(/\s+/g, ' ').trim();

/**
 * Cut without splitting an astral character in half — the same guard the
 * envelope's `capped` helper applies to a selection.
 */
const cutSafely = (value: string, max: number): string => {
  if (value.length <= max) return value;

  const cut = value.slice(0, max);
  const lastCode = cut.charCodeAt(cut.length - 1);

  return lastCode >= 0xd800 && lastCode <= 0xdbff ? cut.slice(0, -1) : cut;
};

const clampLabel = (value: string): string => cutSafely(value, MAX_LABEL_CHARS);
const tagOf = (element: Element): string => element.tagName.toLowerCase();

const attr = (element: Element, name: string): string =>
  collapse(element.getAttribute(name) ?? '');

const inputType = (element: Element): string | null =>
  tagOf(element) === 'input'
    ? (element.getAttribute('type') ?? 'text').toLowerCase()
    : null;

/**
 * Is the element rendered? `checkVisibility` answers it outright where it
 * exists; jsdom (and older Safari) does not have it, so the fallback checks the
 * two properties that actually hide content, inline first so the common case
 * never reaches the style engine.
 */
const isInvisible = (element: Element): boolean => {
  const withCheck = element as Element & {
    checkVisibility?: (options?: { visibilityProperty?: boolean }) => boolean;
  };

  if (typeof withCheck.checkVisibility === 'function') {
    return !withCheck.checkVisibility({ visibilityProperty: true });
  }

  const inline = (element as HTMLElement).style;

  if (inline && (inline.display === 'none' || inline.visibility === 'hidden')) {
    return true;
  }

  const view = element.ownerDocument?.defaultView;

  if (!view) return false;

  const computed = view.getComputedStyle(element);

  return computed.display === 'none' || computed.visibility === 'hidden';
};

const shouldSkipElement = (element: Element): boolean => {
  const tag = tagOf(element);

  if (SKIPPED_TAGS.has(tag)) return true;
  if (element.hasAttribute('hidden')) return true;
  if (element.getAttribute('aria-hidden') === 'true') return true;
  if (element.getAttribute('data-chat-snapshot') === 'exclude') return true;
  if (inputType(element) === 'password') return true;

  return isInvisible(element);
};

/**
 * Is `element` readable from `boundary` — inside it, with nothing excluded,
 * hidden or invisible in between?
 *
 * Descending gets this for free, so the walk never needs it. Two things do not
 * descend: a label reached by id (`aria-labelledby`, `label[for]`), which jumps
 * sideways across the document, and a table row reached through the table,
 * which skips over its row group. Without the check the drawer's own text could
 * re-enter through a label reference, and a `display: none` `<tbody>` would be
 * read out in full.
 */
const isReadableWithin = (element: Element, boundary: Element): boolean => {
  let current: Element | null = element;

  while (current && current !== boundary) {
    if (shouldSkipElement(current)) return false;

    current = current.parentElement;
  }

  return current === boundary;
};

/** Has a budget run out? Checked on entry to every node. */
const isOverBudget = (state: WalkState): boolean => {
  if (state.stopped) return true;

  state.nodes += 1;

  if (state.nodes > state.maxNodes) {
    state.truncated = true;
    state.stopped = true;
    return true;
  }

  if (
    state.nodes % NODE_CHECK_INTERVAL === 0 &&
    state.now() >= state.deadline
  ) {
    state.truncated = true;
    state.stopped = true;
    return true;
  }

  return false;
};

/**
 * Line shapes that name an interactive control. Two of them in a row are two
 * controls, not a repeat: on a phone a pair of confirmation cards both show
 * "Continue", and collapsing that into one line told the companion there was one
 * button to press when there were two (ORA-750 review round 2).
 */
const CONTROL_LINE_PREFIXES = ['[button:', '[link:', '[tab:'];

const isControlLine = (line: string): boolean =>
  CONTROL_LINE_PREFIXES.some((prefix) => line.startsWith(prefix));

/**
 * Commit one line: collapsed, clamped, de-duplicated against the line before
 * it, and inside the character budget.
 *
 * The cap lands on a line boundary — a line that does not fit is dropped whole
 * rather than cut mid-sentence — and dropping one line does not end the walk.
 * A single long paragraph early on must not cost the student every heading and
 * figure after it; the walk stops only once too little budget remains for any
 * line to be worth reading.
 */
const pushLine = (state: WalkState, raw: string): void => {
  if (state.stopped) return;

  const collapsed = collapse(raw);

  if (!collapsed) return;

  const line =
    collapsed.length > MAX_LINE_CHARS
      ? `${cutSafely(collapsed, MAX_LINE_CHARS)}…`
      : collapsed;

  if (state.lines[state.lines.length - 1] === line && !isControlLine(line)) {
    return;
  }

  // Room for the truncation marker is held back, so it can always be appended.
  const budget = state.maxChars - TRUNCATION_LINE.length - 1;
  const separator = state.lines.length === 0 ? 0 : 1;
  const remaining = budget - state.chars - separator;

  if (line.length > remaining) {
    state.truncated = true;

    if (remaining < MIN_LINE_BUDGET) state.stopped = true;

    return;
  }

  state.lines.push(line);
  state.chars += separator + line.length;
};

/** Close the line being built out of inline text, if there is one. */
const flush = (state: WalkState): void => {
  const pending = state.pending;

  state.pending = '';

  if (pending) pushLine(state, pending);
};

/**
 * The visible text of a subtree, as one collapsed string. Every element
 * boundary contributes a space so neighbouring elements cannot be glued into a
 * single word; whitespace collapsing takes care of the rest.
 */
const textOf = (element: Element, state: WalkState): string => {
  let text = '';

  const collect = (node: Node): void => {
    if (node.nodeType === TEXT_NODE) {
      text += node.nodeValue ?? '';
      return;
    }

    if (node.nodeType !== ELEMENT_NODE) return;
    if (isOverBudget(state)) return;
    if (shouldSkipElement(node as Element)) return;

    text += ' ';
    node.childNodes.forEach(collect);
    text += ' ';
  };

  element.childNodes.forEach(collect);

  return collapse(text);
};

/** The text of every element an `aria-labelledby` points at. */
const labelledByText = (element: Element, state: WalkState): string => {
  const ids = element.getAttribute('aria-labelledby');

  if (!ids) return '';

  const doc = element.ownerDocument;

  if (!doc) return '';

  return collapse(
    ids
      .split(/\s+/)
      .map((id) => doc.getElementById(id))
      .filter((target): target is HTMLElement =>
        Boolean(target && isReadableWithin(target, state.root))
      )
      .map((target) => textOf(target, state))
      .join(' ')
  );
};

/** The `<label>` bound to a form control, by `for` or by wrapping it. */
const associatedLabelText = (element: Element, state: WalkState): string => {
  const id = element.getAttribute('id');

  if (id) {
    try {
      const label = element.ownerDocument?.querySelector(
        `label[for="${CSS.escape(id)}"]`
      );

      if (label && isReadableWithin(label, state.root)) {
        return textOf(label, state);
      }
    } catch {
      // A selector the engine will not take (or no CSS.escape) is not a label.
    }
  }

  const wrapping = element.closest?.('label');

  return wrapping && isReadableWithin(wrapping, state.root)
    ? textOf(wrapping, state)
    : '';
};

/**
 * Does this text name anything a student could read back to us?
 *
 * A control whose only visible text is a glyph — "›", "×", "⋯" — is an icon
 * that happens to be drawn with a character rather than an `<svg>`, and its
 * `aria-label` is the only name it has. Requiring one letter or digit keeps
 * those on the accessible name while real words still win (ORA-750).
 */
const namesSomething = (value: string): boolean => /[\p{L}\p{N}]/u.test(value);
/** Words in an already-collapsed string. */
const wordCount = (value: string): number => value.split(' ').length;

/**
 * Is the visible text a TRUNCATION of the accessible name rather than a name of
 * its own (ORA-750)?
 *
 * The Home cards show "Start QBank" on a wide screen and just "Start" below
 * 560px, with `aria-label="Start qbank session"` throughout. Preferring the
 * visible text there gave the companion two identical `[button: Start]` lines —
 * deduplicated into one — and no way to tell the QBank card from the Flashcards
 * one. So a short visible label that the accessible name spells out in full
 * loses to the accessible name: same name, with the object restored.
 *
 * The test is a case-sensitive PREFIX, and both halves of that matter.
 *
 * A prefix, because a label that merely mentions the visible text somewhere is
 * describing the control rather than expanding its name: "Navigate to Topics"
 * over a button that says "Topics", "Go to your QBank" over a link that says
 * "QBank", "Select Cardiology" over a chip that says "Cardiology". The student
 * reads the short word, and the sentence wrapped around it is not what they
 * would say back to us.
 *
 * Case-sensitive, because that is what keeps the wide-screen Home case working:
 * "Start qbank session" is not a prefix-match for "Start QBank" — the product
 * name is capitalised on the control and not in the label. A label written in
 * the control's own words is an expansion of it; one that only lines up when you
 * flatten the case is separate prose.
 */
const isTruncatedLabel = (visible: string, ariaLabel: string): boolean =>
  ariaLabel !== '' &&
  ariaLabel !== visible &&
  wordCount(visible) <= 2 &&
  ariaLabel.startsWith(visible);

/**
 * What to call a control. Anything with no name at all is left out entirely:
 * `[button: ]` tells the companion nothing and costs a line.
 *
 * The VISIBLE text comes first, and that ordering is the whole point of this
 * function (ORA-750). It used to read `aria-label` first, so the companion was
 * told the button said "Start qbank session" while the student was looking at
 * "Start QBank", and on the QBank page it was handed "Start QBank" — an
 * `aria-label` on the per-topic play icons — while the button under the
 * student's thumb said "Start Block". 39% of its answers to "how do I / where
 * is" questions were wrong or partly wrong, and being told to press a control
 * that does not exist by that name is a large part of why: the accessible name
 * is written for a screen reader, not for someone reading the screen.
 *
 * So the accessible name is now the fallback it should always have been, for
 * the controls that have no visible text of their own — an icon button, a bare
 * `<input>` — and for the ones whose visible text is only a truncation of it
 * (see `isTruncatedLabel`, which is what keeps a phone's "Start" buttons apart).
 * Only one name is emitted, never both: the line format has no channel for a
 * second (`(selected)` / `(on)` are per-role state, not a general attribute
 * list), and inventing one would put the very string that misled the model back
 * in front of it.
 *
 * Reading the text first costs the walk a few nodes on controls that would
 * previously have stopped at the attribute. It is bounded by the control's own
 * subtree, which the walk does not descend into afterwards either way.
 */
const labelFor = (element: Element, state: WalkState): string => {
  // A `<select>`'s text content is its whole option list, which names the
  // choices rather than the control; `valueOf` reports the selected one.
  const own = tagOf(element) === 'select' ? '' : textOf(element, state);
  const ariaLabel = attr(element, 'aria-label');

  if (own && namesSomething(own) && !isTruncatedLabel(own, ariaLabel)) {
    return clampLabel(own);
  }

  if (ariaLabel) return clampLabel(ariaLabel);

  const labelledBy = labelledByText(element, state);

  if (labelledBy) return clampLabel(labelledBy);

  const associated = associatedLabelText(element, state);

  if (associated) return clampLabel(associated);

  // A glyph is still better than nothing once every name has been tried.
  if (own) return clampLabel(own);

  return clampLabel(attr(element, 'title'));
};

/** What a form control currently holds, capped. */
const valueOf = (element: Element): string => {
  if (tagOf(element) === 'select') {
    const select = element as HTMLSelectElement;
    const option = select.selectedOptions?.[0] ?? select.options?.[0];

    return option ? collapse(option.textContent ?? '') : '';
  }

  const value = (element as HTMLInputElement | HTMLTextAreaElement).value ?? '';

  return cutSafely(collapse(value), MAX_INPUT_VALUE_CHARS);
};

const isChecked = (element: Element): boolean => {
  const ariaChecked = element.getAttribute('aria-checked');

  if (ariaChecked !== null) return ariaChecked === 'true';

  return (element as HTMLInputElement).checked === true;
};

/**
 * The line an interactive element serializes to.
 *
 * `undefined` means "not a control, walk it normally"; `null` means "a control
 * with no name, drop it".
 */
const controlLine = (
  element: Element,
  state: WalkState
): string | null | undefined => {
  const tag = tagOf(element);
  const role = element.getAttribute('role');
  const type = inputType(element);

  const named = (): string | null => {
    const label = labelFor(element, state);

    return label || null;
  };

  if (role === 'tab') {
    const label = named();

    if (!label) return null;

    return element.getAttribute('aria-selected') === 'true'
      ? `[tab: ${label} (selected)]`
      : `[tab: ${label}]`;
  }

  if (role === 'switch' || type === 'checkbox' || type === 'radio') {
    const label = named();

    if (!label) return null;

    return `[switch: ${label} (${isChecked(element) ? 'on' : 'off'})]`;
  }

  if (role === 'progressbar') {
    const label = named();

    if (!label) return null;

    const valueNow = attr(element, 'aria-valuenow');

    return valueNow
      ? `[progress: ${label} ${valueNow}%]`
      : `[progress: ${label}]`;
  }

  if (role === 'img') {
    // A chart is only readable through its own description; one without a
    // description is just a box, so let the walk carry on into it.
    const label = attr(element, 'aria-label');

    return label ? `[chart: ${clampLabel(label)}]` : undefined;
  }

  if (tag === 'button' || role === 'button') {
    const label = named();

    return label ? `[button: ${label}]` : null;
  }

  if (tag === 'a' && element.hasAttribute('href')) {
    const label = named();

    return label ? `[link: ${label}]` : null;
  }

  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    const label = named();
    const value = valueOf(element);

    // An unnamed `<select>` still shows its selected option on screen, and that
    // is the half a student is likely to be asking about.
    if (!label)
      return tag === 'select' && value ? `[input: select = ${value}]` : null;

    return value ? `[input: ${label} = ${value}]` : `[input: ${label}]`;
  }

  return undefined;
};

/**
 * An opt-in annotation: `data-chat-label` names a value the DOM does not put
 * next to it (a ring, a sparkline, a figure whose caption sits elsewhere), and
 * `data-chat-value` overrides what the element itself reads as.
 */
const annotationLine = (element: Element, state: WalkState): string | null => {
  const label = attr(element, 'data-chat-label');

  if (!label) return null;

  const override = element.getAttribute('data-chat-value');
  const value = override !== null ? collapse(override) : textOf(element, state);

  return value ? `${label}: ${value}` : label;
};

const cellsOf = (row: Element): Element[] =>
  Array.from(row.children).filter(
    (cell) =>
      (tagOf(cell) === 'td' || tagOf(cell) === 'th') && !shouldSkipElement(cell)
  );

const rowLine = (row: Element, state: WalkState): string | null => {
  const cells = cellsOf(row);

  if (cells.length === 0) return null;

  return `| ${cells.map((cell) => textOf(cell, state)).join(' | ')} |`;
};

/** A table as markdown rows: header first when it has one, body rows capped. */
const emitTable = (table: Element, state: WalkState): void => {
  // `querySelectorAll` jumps straight past the row groups, so each row is
  // checked back up to the table: a `display: none` `<tbody>` hides its rows.
  const rows = Array.from(table.querySelectorAll('tr')).filter((row) =>
    isReadableWithin(row, table)
  );

  const headerRows: Element[] = [];
  const bodyRows: Element[] = [];

  rows.forEach((row) => {
    const cells = cellsOf(row);

    const isHeader =
      bodyRows.length === 0 &&
      cells.length > 0 &&
      cells.every((cell) => tagOf(cell) === 'th');

    (isHeader ? headerRows : bodyRows).push(row);
  });

  headerRows.forEach((row) => {
    if (isOverBudget(state)) return;

    const line = rowLine(row, state);

    if (line) pushLine(state, line);
  });

  bodyRows.slice(0, MAX_TABLE_ROWS).forEach((row) => {
    if (isOverBudget(state)) return;

    const line = rowLine(row, state);

    if (line) pushLine(state, line);
  });

  if (bodyRows.length > MAX_TABLE_ROWS) {
    pushLine(state, `… ${bodyRows.length - MAX_TABLE_ROWS} more rows`);
  }
};

const emitList = (list: Element, state: WalkState): void => {
  const items = Array.from(list.children).filter(
    (item) => tagOf(item) === 'li' && !shouldSkipElement(item)
  );

  items.slice(0, MAX_LIST_ITEMS).forEach((item) => {
    if (isOverBudget(state)) return;

    const text = textOf(item, state);

    if (text) pushLine(state, `- ${text}`);
  });

  if (items.length > MAX_LIST_ITEMS) {
    pushLine(state, `… ${items.length - MAX_LIST_ITEMS} more items`);
  }
};

const visit = (node: Node, state: WalkState): void => {
  if (node.nodeType === TEXT_NODE) {
    if (isOverBudget(state)) return;

    state.pending += node.nodeValue ?? '';
    return;
  }

  if (node.nodeType !== ELEMENT_NODE) return;
  if (isOverBudget(state)) return;

  const element = node as Element;

  if (shouldSkipElement(element)) return;

  const annotation = annotationLine(element, state);

  if (annotation !== null) {
    flush(state);
    pushLine(state, annotation);
    return;
  }

  const tag = tagOf(element);
  const headingLevel = HEADING_LEVELS[tag];

  if (headingLevel) {
    flush(state);

    const text = textOf(element, state);

    if (text) pushLine(state, `${'#'.repeat(headingLevel)} ${text}`);
    return;
  }

  if (tag === 'table') {
    flush(state);
    emitTable(element, state);
    return;
  }

  if (tag === 'ul' || tag === 'ol') {
    flush(state);
    emitList(element, state);
    return;
  }

  // A wrapping `<label>` is the control's name, not a line of its own:
  // serializing both repeats the text the control line already carries.
  if (tag === 'label') {
    const labelled = element.querySelector('input, select, textarea');

    if (labelled && !shouldSkipElement(labelled)) {
      flush(state);
      visit(labelled, state);
      return;
    }
  }

  const control = controlLine(element, state);

  if (control !== undefined) {
    flush(state);

    if (control) pushLine(state, control);
    return;
  }

  if (BLOCK_TAGS.has(tag)) {
    flush(state);
    element.childNodes.forEach((child) => visit(child, state));
    flush(state);
    return;
  }

  // Inline: a space at each boundary, so two elements cannot read as one word.
  state.pending += ' ';
  element.childNodes.forEach((child) => visit(child, state));
  state.pending += ' ';
};

const defaultNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const defaultRoot = (): Element | null =>
  typeof document === 'undefined'
    ? null
    : document.getElementById('main-content');

/**
 * Serialize what the page is showing, or null when there is nothing worth
 * sending (no container, a container that renders to almost nothing).
 */
export const captureScreenSnapshot = (
  options: ScreenSnapshotOptions = {}
): string | null => {
  const root = options.root === undefined ? defaultRoot() : options.root;

  if (!root) return null;

  const now = options.now ?? defaultNow;

  const state: WalkState = {
    root,
    lines: [],
    chars: 0,
    pending: '',
    nodes: 0,
    truncated: false,
    stopped: false,
    maxChars: options.maxChars ?? CHAT_SCREEN_MAX_CHARS,
    maxNodes: options.maxNodes ?? DEFAULT_MAX_NODES,
    deadline: now() + (options.timeBudgetMs ?? DEFAULT_TIME_BUDGET_MS),
    now,
  };

  visit(root, state);
  flush(state);

  if (state.truncated) {
    state.lines.push(TRUNCATION_LINE);
  }

  const text = state.lines.join('\n');

  return text.length < MIN_USEFUL_CHARS ? null : text;
};
